import type { CardConfig } from '../models/card-model';
import { CardType } from '../models/card-model';
import type { PaletteConfig } from '../models/chart-config.model';

/**
 * Palette id sent on `updateCard.patch.paletteId` (string).
 * When authoring custom overrides (`useDefaultPalette === false`), omit patch id —
 * custom colors remain in nested `palette` beyond this wire field.
 *
 * Supports root `paletteId`, legacy root `palette` string, or nested `PaletteConfig`.
 */
export function paletteIdForCardPatch(
	card: Partial<CardConfig> | undefined
): string | undefined {
	if (!card) return undefined;
	const rec = card as Record<string, unknown>;
	const rawObj = rec.palette;
	if (rawObj && typeof rawObj === 'object' && !Array.isArray(rawObj)) {
		const pal = rawObj as PaletteConfig;
		if (pal.useDefaultPalette === false) {
			return undefined;
		}
		if (pal.inheritsReportThemePalette === true) {
			return undefined;
		}
		const id =
			typeof pal.selectedPaletteId === 'string'
				? pal.selectedPaletteId.trim()
				: '';
		return id.length > 0 ? id : undefined;
	}
	if (typeof rawObj === 'string') {
		const s = rawObj.trim();
		return s.length > 0 ? s : undefined;
	}
	const pid = rec.paletteId;
	if (typeof pid === 'string') {
		const s = pid.trim();
		return s.length > 0 ? s : undefined;
	}
	return undefined;
}

function isChartLikeCard(cardType: unknown): boolean {
	return cardType === CardType.Chart || cardType === 'chart';
}

/**
 * In-place: GraphQL `getCard` may return `paletteId` (or legacy `palette` string);
 * chart editor state expects nested `palette` ({@link PaletteConfig}).
 * Drops `paletteId` after merging. Missing or empty id → {@link PaletteConfig.inheritsReportThemePalette}.
 */
export function applyPaletteWireFromApi(
	cfg: Partial<CardConfig> & Record<string, unknown>
): void {
	if (!isChartLikeCard(cfg.cardType)) {
		return;
	}

	if (
		cfg.palette &&
		typeof cfg.palette === 'object' &&
		!Array.isArray(cfg.palette)
	) {
		return;
	}

	let id = '';
	if (typeof cfg.paletteId === 'string') {
		id = cfg.paletteId.trim();
	}
	delete cfg.paletteId;

	if (!id.length && typeof cfg.palette === 'string') {
		id = cfg.palette.trim();
	}
	if (typeof cfg.palette === 'string') {
		delete cfg.palette;
	}

	if (id.length > 0) {
		cfg.palette = {
			selectedPaletteId: id,
			useDefaultPalette: true,
		};
		return;
	}

	cfg.palette = {
		useDefaultPalette: true,
		inheritsReportThemePalette: true,
	};
}
